import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Calendar, Building, Users, TrendingUp } from 'lucide-react';

const ExperienceSection = () => {
  const experiences = [
    {
      title: 'Lead Full Stack Developer',
      company: 'UST (Client: Philips)',
      location: 'Bangalore, India',
      period: 'April 2024 - Present',
      type: 'Full-time',
      description: 'Leading development of CT-Eye analytics platform using Spring Boot microservices and ReactJS.',
      achievements: [
        'Architected S3-backed data pipelines with AWS Lambda, improving data ingestion throughput by 40%',
        'Introduced standardized REST API contracts and automated CI/CD pipelines, reducing release cycle time by 30%',
        'Mentored a team of 5 engineers on AWS best practices and React state management',
        'Designed healthcare analytics systems processing real-time medical data'
      ],
      skills: ['Spring Boot', 'ReactJS', 'AWS Lambda', 'S3', 'Microservices', 'Healthcare Analytics'],
      color: 'from-blue-500 to-indigo-600',
      icon: '🏥'
    },
    {
      title: 'Senior Software Engineer',
      company: 'Quest Global (Client: HPE)',
      location: 'Bengaluru, India',
      period: 'August 2022 - March 2023',
      type: 'Full-time',
      description: 'Developed Material Shortage Planning (MSP) application integrating predictive analytics.',
      achievements: [
        'Built MSP application using Java Spring Boot and ReactJS with AWS integration',
        'Designed modular microservices architecture with AWS CloudFormation for scalability',
        'Implemented comprehensive testing achieving 85% code coverage with JUnit and Jest',
        'Integrated predictive shortage analytics with AWS S3 and Elasticsearch'
      ],
      skills: ['Java Spring Boot', 'ReactJS', 'AWS CloudFormation', 'Elasticsearch', 'JUnit', 'Jest'],
      color: 'from-green-500 to-emerald-600',
      icon: '💼'
    },
    {
      title: 'Senior Software Engineer',
      company: 'Forward AI',
      location: 'Remote',
      period: 'November 2021 - January 2022',
      type: 'Contract',
      description: 'Built AutoAudit quality-management software deployed on AWS infrastructure.',
      achievements: [
        'Developed AutoAudit software using Node.js, Express.js, and ReactJS',
        'Deployed on AWS EC2 with S3 storage for scalable file management',
        'Enhanced application reliability with retry logic and circuit-breaker patterns',
        'Decreased error rates by 25% through improved error handling'
      ],
      skills: ['Node.js', 'Express.js', 'ReactJS', 'AWS EC2', 'S3', 'Circuit Breaker Pattern'],
      color: 'from-purple-500 to-violet-600',
      icon: '🤖'
    },
    {
      title: 'Software Developer',
      company: 'Bacancy Technologies',
      location: 'Ahmedabad, India',
      period: 'May 2021 - November 2021',
      type: 'Full-time',
      description: 'Implemented CustodyRx hospital asset-management modules with .NET Core and ReactJS.',
      achievements: [
        'Built hospital asset-management system with .NET Core and ReactJS',
        'Integrated with MySQL databases for efficient data management',
        'Improved inventory accountability and reduced resource misplacement by 20%',
        'Developed comprehensive reporting and tracking features'
      ],
      skills: ['.NET Core', 'ReactJS', 'MySQL', 'Asset Management', 'Healthcare Systems'],
      color: 'from-orange-500 to-red-600',
      icon: '🏥'
    }
  ];

  return (
    <section id="experience" className="py-20 relative">
      <div className="container mx-auto px-6 z-10 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Professional <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Experience</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            7+ years of building scalable applications and leading development teams across healthcare, enterprise, and AI domains
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {experiences.map((exp, index) => (
            <Card key={index} className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm overflow-hidden">
              <div className="p-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-3">{exp.icon}</span>
                      <h3 className="text-2xl font-bold text-white">{exp.title}</h3>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-gray-300 mb-4">
                      <div className="flex items-center">
                        <Building className="w-4 h-4 mr-2" />
                        <span className="font-semibold">{exp.company}</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span>{exp.location}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>{exp.period}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Badge 
                    className={`bg-gradient-to-r ${exp.color} text-white px-3 py-1 mt-4 lg:mt-0`}
                  >
                    {exp.type}
                  </Badge>
                </div>

                <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                  {exp.description}
                </p>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-white flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-green-400" />
                    Key Achievements
                  </h4>
                  
                  <ul className="space-y-2">
                    {exp.achievements.map((achievement, achIndex) => (
                      <li key={achIndex} className="flex items-start text-gray-300">
                        <span className="text-blue-400 mr-3 mt-2">•</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-700/50">
                  <h4 className="text-sm font-semibold text-gray-400 mb-3">Technologies Used:</h4>
                  <div className="flex flex-wrap gap-2">
                    {exp.skills.map((skill, skillIndex) => (
                      <Badge 
                        key={skillIndex} 
                        variant="outline" 
                        className="border-gray-600 text-gray-300 hover:border-blue-400 hover:text-blue-400 transition-colors duration-200"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Leadership Stats */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm text-center p-6">
            <Users className="w-8 h-8 mx-auto text-blue-400 mb-4" />
            <div className="text-3xl font-bold text-white mb-2">15+</div>
            <div className="text-gray-400">Engineers Mentored</div>
          </Card>
          
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm text-center p-6">
            <TrendingUp className="w-8 h-8 mx-auto text-green-400 mb-4" />
            <div className="text-3xl font-bold text-white mb-2">40%</div>
            <div className="text-gray-400">Avg Performance Improvement</div>
          </Card>
          
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm text-center p-6">
            <Building className="w-8 h-8 mx-auto text-purple-400 mb-4" />
            <div className="text-3xl font-bold text-white mb-2">5+</div>
            <div className="text-gray-400">Companies Worked With</div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;